import type { ChartWrapperOptions } from 'react-google-charts'
import type { GanttChartTemplateProps } from 'sitemodel/schema/components/GanttChartSchema'

const DAY_MS = 24 * 60 * 60 * 1000

export type GoogleGanttDataTable = unknown[][]

export type GanttCockpitCategoryOverride = {
  id: string
  label?: string
  backgroundColor?: string
}

export type GanttCockpitChartOptions = {
  gantt_categories?: GanttCockpitCategoryOverride[]
  gantt_arrow_overrides?: Array<{
    fromCardId: string
    toCardId: string
    originAnchor?: 'bottom-start' | 'bottom-center'
  }>
  gantt_footer?: GanttChartTemplateProps['footer']
  gantt_sidebar_title?: string
}

const COLUMN_ALIASES = {
  taskId: ['task id'],
  taskName: ['task name'],
  resource: ['resource'],
  start: ['start date', 'start'],
  end: ['end date', 'end'],
  duration: ['duration'],
  percent: ['percent complete', 'percent'],
  dependencies: ['dependencies', 'dependency'],
  rowIndex: ['row index', 'lane'],
  categoryId: ['category id'],
  categoryColor: ['category color']
} as const

type ColumnKey = keyof typeof COLUMN_ALIASES
type Columns = Record<ColumnKey, number>

const normalizeHeader = (value: unknown) =>
  String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

const toIsoDate = (value: unknown): string | null => {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : value.toISOString().slice(0, 10)
  }
  const s = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const parsed = new Date(s)
  return Number.isNaN(parsed.getTime())
    ? null
    : parsed.toISOString().slice(0, 10)
}

const addDaysIso = (isoDate: string, days: number): string | null => {
  const base = new Date(`${isoDate}T00:00:00.000Z`).getTime()
  return Number.isNaN(base)
    ? null
    : new Date(base + days * DAY_MS).toISOString().slice(0, 10)
}

const captionIdFromPercent = (value: unknown): string | undefined => {
  const n = Number(value)
  if (Number.isNaN(n)) return undefined
  if (n >= 100) return 'finished'
  if (n <= 0) return 'starting'
  return 'in-progress'
}

const clampRowIndex = (value: unknown): number => {
  const n = Number(value)
  if (Number.isNaN(n)) return 0
  return Math.min(15, Math.max(0, Math.floor(n)))
}

const splitDependencies = (value: unknown): string[] => {
  if (value == null || value === '') return []
  return String(value)
    .split(/[,;]/g)
    .map((s) => s.trim())
    .filter(Boolean)
}

const syntheticCategoryId = (resource: string, used: Set<string>): string => {
  const base =
    resource
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9-_]/g, '')
      .toLowerCase() || 'default'
  let id = `res-${base}`
  let n = 0
  while (used.has(id)) {
    n += 1
    id = `res-${base}-${n}`
  }
  used.add(id)
  return id
}

const resolveColumns = (headerRow: unknown[]): Columns | null => {
  const headers = headerRow.map(normalizeHeader)
  const columns = Object.fromEntries(
    (Object.entries(COLUMN_ALIASES) as [ColumnKey, readonly string[]][]).map(
      ([key, aliases]) => {
        const found = aliases
          .map((alias) => headers.indexOf(alias))
          .find((i) => i >= 0)
        return [key, found ?? -1]
      }
    )
  ) as Columns

  const hasRequired =
    columns.taskId >= 0 &&
    columns.taskName >= 0 &&
    columns.resource >= 0 &&
    columns.start >= 0 &&
    (columns.end >= 0 || columns.duration >= 0)

  return hasRequired ? columns : null
}

const inferYear = (cards: GanttChartTemplateProps['cards']): number => {
  let min = Infinity
  for (const c of cards) {
    for (const t of [new Date(c.start).getTime(), new Date(c.end).getTime()]) {
      if (!Number.isNaN(t)) min = Math.min(min, t)
    }
  }
  return min === Infinity
    ? new Date().getUTCFullYear()
    : new Date(min).getUTCFullYear()
}

export const mergeGanttCategoryPresentation = (
  categories: GanttChartTemplateProps['categories'],
  overrides?: GanttCockpitCategoryOverride[]
): GanttChartTemplateProps['categories'] => {
  if (!overrides?.length) return categories
  const byId = new Map(overrides.map((o) => [o.id, o]))
  return categories.map((category) => {
    const override = byId.get(category.id)
    if (!override) return category
    return {
      ...category,
      ...(override.label !== undefined ? { label: override.label } : {}),
      ...(override.backgroundColor !== undefined
        ? { backgroundColor: override.backgroundColor }
        : {})
    }
  })
}

export type AdaptedGoogleGanttTemplateProps = {
  year: number
  categories: GanttChartTemplateProps['categories']
  cards: GanttChartTemplateProps['cards']
  arrows?: GanttChartTemplateProps['arrows']
}

export const adaptGoogleGanttDataTableToPartialProps = (
  data: unknown,
  options?: ChartWrapperOptions['options']
): AdaptedGoogleGanttTemplateProps | null => {
  if (!Array.isArray(data) || data.length < 2 || !Array.isArray(data[0])) {
    return null
  }

  const columns = resolveColumns(data[0])
  if (!columns) return null

  const usedSynthIds = new Set<string>()
  const resourceToSynthId = new Map<string, string>()
  const categoryInfo = new Map<
    string,
    { label: string; backgroundColor?: string }
  >()

  const registerCategory = (id: string, label: string, color?: string) => {
    const existing = categoryInfo.get(id)
    if (existing) {
      if (!existing.backgroundColor && color) existing.backgroundColor = color
      return
    }
    categoryInfo.set(id, {
      label: label.trim() || '—',
      ...(color ? { backgroundColor: color } : {})
    })
  }

  const cards: GanttChartTemplateProps['cards'] = []
  const arrows: NonNullable<GanttChartTemplateProps['arrows']> = []

  for (let r = 1; r < data.length; r += 1) {
    const row = data[r]
    if (!Array.isArray(row)) continue

    const rawId = row[columns.taskId]
    if (rawId == null || String(rawId).trim() === '') continue

    const id = String(rawId).trim()
    const label = String(row[columns.taskName] ?? '').trim() || id
    const resource = String(row[columns.resource] ?? '').trim() || ' '
    const colorFromRow =
      String(row[columns.categoryColor] ?? '').trim() || undefined

    const explicitCategoryId = String(row[columns.categoryId] ?? '').trim()
    let categoryId: string
    if (explicitCategoryId) {
      categoryId = explicitCategoryId
    } else {
      categoryId =
        resourceToSynthId.get(resource) ??
        syntheticCategoryId(resource, usedSynthIds)
      resourceToSynthId.set(resource, categoryId)
    }
    registerCategory(categoryId, resource, colorFromRow)

    const start = toIsoDate(row[columns.start])
    if (!start) continue

    let end = toIsoDate(row[columns.end])
    if (!end) {
      const d = Number(row[columns.duration])
      if (!Number.isNaN(d)) end = addDaysIso(start, d)
    }
    if (!end) continue

    cards.push({
      id,
      categoryId,
      rowIndex: clampRowIndex(row[columns.rowIndex]),
      start,
      end,
      label,
      captionId: captionIdFromPercent(row[columns.percent])
    })

    splitDependencies(row[columns.dependencies]).forEach((predecessorId, i) => {
      arrows.push({
        id: `dep-${predecessorId}-${id}-${i}`,
        fromCardId: predecessorId,
        toCardId: id
      })
    })
  }

  if (!cards.length) return null

  const categories: GanttChartTemplateProps['categories'] = Array.from(
    categoryInfo,
    ([id, meta]) => ({
      id,
      label: meta.label,
      ...(meta.backgroundColor ? { backgroundColor: meta.backgroundColor } : {})
    })
  )

  const cockpitOptions = options as
    | (GanttCockpitChartOptions & { year?: unknown })
    | undefined

  const arrowOverrides = cockpitOptions?.gantt_arrow_overrides
  const finalArrows = arrowOverrides?.length
    ? arrows.map((arrow) => {
        const match = arrowOverrides.find(
          (o) =>
            o.fromCardId === arrow.fromCardId && o.toCardId === arrow.toCardId
        )
        return match?.originAnchor
          ? { ...arrow, originAnchor: match.originAnchor }
          : arrow
      })
    : arrows

  const optYear =
    typeof cockpitOptions?.year === 'number' ? cockpitOptions.year : undefined

  return {
    year: optYear ?? inferYear(cards),
    categories,
    cards,
    ...(finalArrows.length ? { arrows: finalArrows } : {})
  }
}

export const buildGanttCockpitTemplateProps = (
  data: unknown,
  options?: ChartWrapperOptions['options']
): GanttChartTemplateProps | null => {
  const adapted = adaptGoogleGanttDataTableToPartialProps(data, options)
  if (!adapted?.categories?.length || !adapted.cards?.length) return null

  const cockpitOptions = options as GanttCockpitChartOptions | undefined

  return {
    ...adapted,
    categories: mergeGanttCategoryPresentation(
      adapted.categories,
      cockpitOptions?.gantt_categories
    ),
    cards: adapted.cards,
    arrows: adapted.arrows ?? [],
    sidebarTitle: cockpitOptions?.gantt_sidebar_title,
    footer: cockpitOptions?.gantt_footer
  }
}
